package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.ReviewStatus
import dev.lingvo.shared.TranslationState
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LFilterIcon
import dev.lingvo.site.ds.LGitBranchIcon
import dev.lingvo.site.ds.LProgressCounts
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LToggleChip
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun EditorQueueFilters(
    state: TranslationEditorState,
    onDone: () -> Unit,
) {
    Column(
        EditorQueueFilterSurfaceStyle.toModifier()
            .attr("data-editor-filter-surface", "true")
            // What the style's phase rules key off. See `EditorQueueFilterSurfaceStyle`.
            .attr("data-presence", LocalQueueFilterPhase.current.attributeValue),
    ) {
        Row(
            Modifier.width(100.percent).gap(Space.XS),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            H2(EditorQueueFilterHeadingStyle.toModifier().flexGrow(1).toAttrs()) {
                // The same glyph the trigger that opened this panel carries
                // (`CompactQueueHeader`'s Filters button). A section heading glyphed in one place
                // and bare in the other is the product spelling one idea two ways; the mark is
                // `aria-hidden` because the word beside it is the whole name.
                Span(Modifier.iconSize(IconSize.MD).toAttrs { attr("aria-hidden", "true") }) {
                    LFilterIcon()
                }
                Text(t("editor.editor_queue_filters.filters", "Filters"))
            }
            if (hasAppliedEditorFilters(state)) {
                LButton(
                    label = t("editor.editor_queue_filters.clear_all", "Clear all"),
                    onClick = {
                        state.clearFilters()
                        onDone()
                    },
                    variant = LButtonVariant.GHOST,
                    size = LButtonSize.SM,
                )
            }
            LButton(
                label = t("editor.editor_queue_filters.done", "Done"),
                onClick = onDone,
                variant = LButtonVariant.SECONDARY,
                size = LButtonSize.SM,
            )
        }
        LSelect(
            value = state.stateFilter.name,
            // Every choice used to be blind until "Done" was pressed: the options carried no
            // counts, so picking one meant discovering afterwards how many strings it matched.
            // `state.progress` already holds the total this project was loaded with — no request
            // added for this. That count is locale-wide, though, so it is only honest for this
            // option once every *other* clause of the query is cleared; with a file, tag, search,
            // review or stale filter also applied, `count` returns null and the option shows no
            // parenthetical rather than a number for a set the option would not actually produce.
            options = EditorStateFilter.entries.map { filter ->
                val count = filter.count(state.progress, state.currentQuery)
                LSelectOption(filter.name, if (count != null) "${filter.label} ($count)" else filter.label)
            },
            onValueChange = { value ->
                EditorStateFilter.entries.firstOrNull { it.name == value }
                    ?.let { state.stateFilter = it }
            },
            modifier = EditorQueueFilterControlStyle.toModifier()
                .attr("data-editor-filter-control", "state"),
            label = t("editor.editor_queue_filters.state", "State"),
        )
        LSelect(
            value = state.fileId,
            options = listOf(LSelectOption("", t("editor.editor_queue_filters.all_files", "All files"))) + state.project.orEmptyFiles(),
            onValueChange = { state.fileId = it },
            modifier = EditorQueueFilterControlStyle.toModifier()
                .attr("data-editor-filter-control", "file"),
            label = t("editor.editor_queue_filters.file", "File"),
            enabled = state.project != null,
        )
        LSelect(
            value = state.tag,
            options = listOf(LSelectOption("", t("editor.editor_queue_filters.all_tags", "All tags"))) + state.availableTags.map {
                LSelectOption(it, it)
            },
            onValueChange = { state.tag = it },
            modifier = EditorQueueFilterControlStyle.toModifier()
                .attr("data-editor-filter-control", "tag"),
            label = t("editor.editor_queue_filters.tag", "Tag"),
            enabled = state.availableTags.isNotEmpty(),
        )
        // **Review and Stale are controls now, not read-only rows.** They arrived by link from the
        // work queue and could only be *removed*, never set — so the one filter a reviewer wants
        // most, "show me what is waiting on me", was reachable from another page and nowhere else.
        // They deliberately do not carry `data-editor-filter-control`: that attribute names the
        // three controls the rendered contract pins in order, and widening it would make the
        // contract mean something new rather than something more.
        LSelect(
            // `NONE` is how a link can spell "no review state at all"; it is not one of the three
            // choices, so it reads here as the unset option rather than as a missing one.
            value = state.review?.takeUnless { it == ReviewStatus.NONE }?.name.orEmpty(),
            options = listOf(
                LSelectOption("", t("editor.editor_queue_filters.any_review_state", "Any review state")),
                LSelectOption(
                    ReviewStatus.PENDING.name,
                    t("editor.editor_queue_filters.awaiting_review", "Awaiting review"),
                ),
                LSelectOption(
                    ReviewStatus.CHANGES_REQUESTED.name,
                    t("editor.editor_queue_filters.changes_requested", "Changes requested"),
                ),
            ),
            onValueChange = { value ->
                state.review = ReviewStatus.entries.firstOrNull { it.name == value }
            },
            modifier = EditorQueueFilterControlStyle.toModifier()
                .attr("data-editor-filter-extra", "review"),
            label = t("editor.editor_queue_filters.review", "Review"),
        )
        LToggleChip(
            text = t("editor.editor_queue_filters.source_changed", "Source changed"),
            selected = state.stale,
            onSelectedChange = { state.stale = it },
            modifier = Modifier.attr("data-editor-filter-extra", "stale"),
            // A branch, because "source changed" is a diff against the string this translation was
            // written from. `count` stays null: no count of stale rows reaches the client —
            // `LocaleProgressDto` carries total/translated/approved and nothing else — and a
            // guessed number here would be exactly the confident zero the header refuses to draw.
            icon = { LGitBranchIcon() },
        )
        // Always visible, filtered or not. It used to appear only when something was applied, so
        // the one question a filter surface exists to answer — how many does this leave me — went
        // unanswered in the state a reader is in most of the time.
        Span(
            EditorQueueFilterResultStyle.toModifier().toAttrs {
                attr("aria-live", "polite")
                attr("data-editor-filter-result", "true")
            },
        ) {
            Text(
                tPlural(
                    "editor.editor_queue_filters.keys_match",
                    state.total,
                    "{count} key matches",
                    "{count} keys match",
                ),
            )
        }
    }
}

/**
 * How many strings this option would show, from the same counts the header's own progress line
 * already reads — [EditorStateFilter.apiState] names which bucket, so there is one place, not four,
 * deciding which count answers "all", "untranslated", "translated" and "approved".
 *
 * Null whenever any *other* clause of [query] is already set: `progress` is locale-wide, so the
 * moment a file, tag, search, review or stale filter narrows the rows, a locale-wide figure next to
 * this option's label describes a set the option would not actually produce if chosen.
 */
internal fun EditorStateFilter.count(progress: LProgressCounts, query: EditorQuery): Int? {
    val otherFilterApplied = query.fileId.isNotBlank() ||
        query.tag.isNotBlank() ||
        query.search.isNotBlank() ||
        (query.review != null && query.review != ReviewStatus.NONE) ||
        query.stale
    if (otherFilterApplied) return null
    return when (apiState) {
        null -> progress.total
        TranslationState.EMPTY -> progress.empty
        TranslationState.TRANSLATED -> progress.translated
        TranslationState.APPROVED -> progress.approved
    }
}

private fun hasAppliedEditorFilters(state: TranslationEditorState): Boolean =
    state.search.isNotBlank() ||
        state.debouncedSearch.isNotBlank() ||
        state.stateFilter != EditorStateFilter.ALL ||
        state.fileId.isNotBlank() ||
        state.tag.isNotBlank() ||
        (state.review != null && state.review != ReviewStatus.NONE) ||
        state.stale

private fun ProjectDetailDto?.orEmptyFiles(): List<LSelectOption> = this?.files.orEmpty()
    .sortedBy { it.path }
    .map { LSelectOption(it.id, it.path) }
